import { faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Autocomplete, Divider } from '@mui/material'
import { IconButton, TextField, Stack } from '@vapor/material'
import { FormItem } from 'hic-components'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTenant } from 'src/app/hooks/context/context.hook'
import HicAmountField from '../../hic-amount-field/hic-amount-field'
import { Payment, PaymentRowError } from './payment-splitter.types'
import { DatePicker } from '@mui/x-date-pickers-pro'
import { useTranslation } from 'react-i18next'

export type PaymentRowProps = {
  numberOfNonEditableRows: number
  index: number
  payments: Payment[]
  amount: number | null
  expireDate?: Date | null
  onAmountChange: (value: number | null) => void
  onAmountBlur: () => void
  paymentActiveId: string | null
  onPaymentIdChange: (paymentActiveId: string | null) => void
  onDeleteRow: (index: number) => void
  startOpen: boolean
  error?: PaymentRowError
  readonly?: boolean
  disabled?: boolean
  disablePriceEdit?: boolean
  showExpirationDate?: boolean
  onExpirationDateChange: (expireDate: Date | null) => void
  disableRowsChange?: boolean
}

export default function PaymentRow({
  index, //
  numberOfNonEditableRows,
  payments,
  amount,
  expireDate,
  onAmountChange,
  onAmountBlur,
  paymentActiveId,
  onPaymentIdChange,
  onDeleteRow,
  startOpen,
  error,
  readonly,
  disabled,
  disablePriceEdit,
  showExpirationDate,
  onExpirationDateChange,
  disableRowsChange,
}: PaymentRowProps) {
  const { currency } = useTenant()
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (startOpen) {
      setOpen(true)
      inputRef.current?.focus()
    }
  }, [])

  const options = useMemo(() => {
    if (payments.some((p) => p.paymentActiveId === paymentActiveId) || paymentActiveId === null) {
      return payments
    }
    return [{ paymentActiveId, description: '-' }, ...payments]
  }, [payments, paymentActiveId])

  const paymentValue = useMemo(() => (paymentActiveId ? options.find((p) => p.paymentActiveId === paymentActiveId) || null : null), [paymentActiveId, options])

  const showDeleteButton: boolean = useMemo(() => index - numberOfNonEditableRows > 0 && !readonly, [index, readonly, numberOfNonEditableRows])

  return (
    <React.Fragment key={index}>
      <Stack spacing={0.5} sx={{ pl: 1, width: '100%', pb: 0.5 }}>
        <Stack direction="row" spacing={2}>
          <Stack direction="row" spacing={2} width={'60%'}>
            <FormItem //
              style={{ width: '60%' }}
              errorMessage={error?.paymentActiveId?.message}
            >
              <Autocomplete
                fullWidth
                disabled={readonly || disabled}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                value={paymentValue}
                onChange={(event, value) => onPaymentIdChange(value?.paymentActiveId || null)}
                options={options}
                renderInput={(params) => {
                  return <TextField {...params} error={error?.paymentActiveId ? true : false} inputRef={inputRef} />
                }}
                getOptionLabel={(option) => option.description}
                isOptionEqualToValue={(option, value) => option.paymentActiveId === value.paymentActiveId}
              />
            </FormItem>

            <FormItem //
              style={{ width: '40%' }}
              errorMessage={error?.amount?.message}
            >
              <HicAmountField //
                fullWidth
                disabled={paymentActiveId === null || readonly || disabled || disablePriceEdit}
                value={amount}
                nonNegative
                currency={currency}
                onChange={onAmountChange}
                onBlur={onAmountBlur}
                error={error?.amount ? true : false}
              />
            </FormItem>
          </Stack>
          <Stack direction="row" spacing={2} justifyContent={showExpirationDate ? 'space-between' : 'flex-end'} width={'40%'} flex={'1 1 auto'}>
            {showExpirationDate && (
              <FormItem //
                style={{ minWidth: '150px' }}
                errorMessage={error?.expireDate?.message}
              >
                <DatePicker //
                  disabled={readonly || disabled}
                  value={expireDate || null}
                  onChange={(date) => {
                    if (typeof date !== 'string') {
                      console.log('DATE CHANGED')
                      onExpirationDateChange(date)
                    }
                  }}
                  disablePast
                  slotProps={{ textField: { inputProps: { placeHolder: t('commons.payment_date') }, error: error?.expireDate ? true : false } }}
                />
              </FormItem>
            )}
            {showDeleteButton && !disableRowsChange && (
              <IconButton disabled={disabled} onClick={() => onDeleteRow(index)}>
                <FontAwesomeIcon icon={faTrash} />
              </IconButton>
            )}
          </Stack>
        </Stack>
        <Divider light />
      </Stack>
    </React.Fragment>
  )
}
